import { useEffect, useState, useCallback } from 'react';
import { CheckCircle, XCircle, AlertCircle, Info, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastMessage {
    id: string;
    message: string;
    type: ToastType;
    duration?: number;
}

interface ToastProps {
    toast: ToastMessage;
    onDismiss: (id: string) => void;
}

const toastStyles: Record<ToastType, { bg: string; icon: typeof CheckCircle; iconColor: string }> = {
    success: {
        bg: 'bg-success-subtle border-success-border',
        icon: CheckCircle,
        iconColor: 'text-success',
    },
    error: {
        bg: 'bg-destructive-subtle border-destructive-border',
        icon: XCircle,
        iconColor: 'text-destructive',
    },
    warning: {
        bg: 'bg-warning-subtle border-warning-border',
        icon: AlertCircle,
        iconColor: 'text-warning',
    },
    info: {
        bg: 'bg-info-subtle border-info-border',
        icon: Info,
        iconColor: 'text-info',
    },
};

const Toast = ({ toast, onDismiss }: ToastProps) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isLeaving, setIsLeaving] = useState(false);
    const style = toastStyles[toast.type];
    const Icon = style.icon;

    const handleDismiss = useCallback(() => {
        setIsLeaving(true);
        setTimeout(() => {
            onDismiss(toast.id);
        }, 300); // Match animation duration
    }, [onDismiss, toast.id]);

    useEffect(() => {
        // Trigger enter animation
        requestAnimationFrame(() => setIsVisible(true));

        // Auto-dismiss
        const duration = toast.duration || 4000;
        const timer = setTimeout(() => {
            handleDismiss();
        }, duration);

        return () => clearTimeout(timer);
    }, [toast.id, toast.duration, handleDismiss]);

    return (
        <div
            role="alert"
            aria-live="polite"
            aria-atomic="true"
            className={cn(
                'flex items-center gap-3 px-4 py-3 rounded-lg border shadow-lg transition-all duration-300 min-w-[300px] max-w-md',
                style.bg,
                isVisible && !isLeaving ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
            )}
        >
            <Icon className={cn('h-5 w-5 flex-shrink-0', style.iconColor)} aria-hidden="true" />
            <p className="flex-1 text-sm font-medium text-foreground">{toast.message}</p>
            <button
                onClick={handleDismiss}
                className="flex-shrink-0 p-1 rounded hover:bg-black/5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label="Dismiss notification"
            >
                <X className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            </button>
        </div>
    );
};

interface ToastContainerProps {
    toasts: ToastMessage[];
    onDismiss: (id: string) => void;
}

export const ToastContainer = ({ toasts, onDismiss }: ToastContainerProps) => {
    if (toasts.length === 0) return null;

    return (
        <div
            className="fixed bottom-4 right-4 z-50 flex flex-col gap-2"
            aria-label="Notifications"
        >
            {toasts.map((toast) => (
                <Toast key={toast.id} toast={toast} onDismiss={onDismiss} />
            ))}
        </div>
    );
};

export default Toast;
